import { createTheme, type Theme } from "@mui/material/styles";
import { breakpoints } from "../tokens/breakpoints";
import { RADIUS_BASE, Shape } from "../tokens/radii";
import { designShadows } from "../tokens/shadows";
import { motionDuration, motionEasing } from "../tokens/motion";
import { zIndex } from "../tokens/zIndex";
import { lightPalette, darkPalette } from "./palette";
import { typography } from "./typography";
import { components } from "./components";
import { RadiusToken } from "../tokens/radii";

// Distribute the 8 design tiers across MUI's 24 elevation slots (3 slots per
// tier) so the natural elevation hierarchy is preserved — Card (1) stays
// subtle, Menu (8) sits in the middle, Drawer (16) is heavier, and Dialog
// (24) is deepest. Without this, the components MUI ships with elevation > 8
// all collapse to the same shadow.
const muiShadowArray = [
  "none",
  designShadows[1],
  designShadows[1],
  designShadows[1], //  1- 3
  designShadows[2],
  designShadows[2],
  designShadows[2], //  4- 6
  designShadows[3],
  designShadows[3],
  designShadows[3], //  7- 9
  designShadows[4],
  designShadows[4],
  designShadows[4], // 10-12
  designShadows[5],
  designShadows[5],
  designShadows[5], // 13-15
  designShadows[6],
  designShadows[6],
  designShadows[6], // 16-18
  designShadows[7],
  designShadows[7],
  designShadows[7], // 19-21
  designShadows[8],
  designShadows[8],
  designShadows[8], // 22-24
] as Theme["shadows"];

export interface CreateRmhThemeOptions {
  /**
   * How light/dark schemes are activated.
   *  - `"media"` (default): follows the user's `prefers-color-scheme` — SSR-safe,
   *    no class plumbing required. Use this in production apps.
   *  - `"class"`: switches based on a `.light` / `.dark` class on a parent
   *    element. Required by tools that need a manual toggle (e.g. Storybook's
   *    `withThemeByClassName` addon).
   */
  colorSchemeSelector?: "media" | "class";
}

export const createRmhTheme = ({
  colorSchemeSelector = "media",
}: CreateRmhThemeOptions = {}): Theme =>
  createTheme({
    cssVariables: {
      cssVarPrefix: "rmh_ds",
      colorSchemeSelector,
    },
    colorSchemes: {
      light: { palette: lightPalette },
      dark: { palette: darkPalette },
    },
    typography,
    components,
    shape: {
      borderRadius: RADIUS_BASE,
      borderMax: RadiusToken.MAX,
      square: Shape.square,
      subtle: Shape.subtle,
      soft: Shape.soft,
      medium: Shape.medium,
      rounded: Shape.rounded,
      large: Shape.large,
      pill: Shape.pill,
    },
    spacing: (factor: number) => `${0.25 * factor}rem`,
    breakpoints: { values: breakpoints },
    shadows: muiShadowArray,
    transitions: { duration: motionDuration, easing: motionEasing },
    zIndex,
  });

export const rmhTheme = createRmhTheme();
